import navigationService from '../../services/NavigationService';
import branchRuleReader from '../../store/readHelpers/BranchRuleReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import questionSelectionService from '../../services/QuestionSelectionService';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../services/a11yIIBScreenReaderService';
import { t } from '../../services/i18nService';
import customFieldsService from '../../services/CustomFieldsService';
import customFieldsConstants from '../../constants/CustomFieldsConstants';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';
import objectTypes from '../../constants/ObjectTypes';
import responseReader from '../../store/readHelpers/ResponseReader';
import footerService from '../../services/FooterService';
import numberingSchemes from '../../constants/NumberingSchemes';

const isA11yIterationForIIBScreenReaderEnabled = () => {
    return a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported();
};

const EMPTY_FOOTER_ATTRIBUTES = Object.freeze({});

const isScorableItemAttempted = (responseStatus, contentRevisionId, scorableItem) => {
    const { scorableItemsStatus = [] } = (responseStatus[contentRevisionId] || {});
    return scorableItemsStatus.some((statusItem) => {
        return scorableItem.orderNumber === statusItem.orderNumber && statusItem.isAttempted === true;
    });
};

const footerSelector = {
    selectShouldRenderNavRow() {
        return settingsReader.showAssessmentTrack() && !branchRuleReader.hasBranchRules() && !sessionReader.isContextReadinessCheck();
    },

    selectIsLargeTest(currentSection, sections) {
        if (sections.length >= 20) {
            return true;
        }

        const totalQuestions = sections.reduce((a, b) => b.questions.length + a, 0);
        if (totalQuestions >= 40) {
            return true;
        }

        const currentSectionQuestionCount = sections.find(section => section.id === currentSection)?.questions.length;
        return currentSectionQuestionCount + sections.length >= 20;
    },

    selectIsNavigationAllowed() {
        return !sessionReader.isOneWayNavigationSupportedContext() || !settingsReader.isOneWayNavigationEnabled();
    },

    shouldShowFooterNavButtons() {
        return !(questionSelectionService.isCandidateSelectedContentBlockEnabled());
    },

    getAttemptsStatusForSection(responseStatus, section, useNumberPerInteraction) {
        const counters = { attempted: 0, total: 0 };
        const { questions } = section;

        if (useNumberPerInteraction) {
            questions.forEach((question) => {
                scorableItemNumberingService
                    .getScorableItemsInQuestion(question.contentRevisionId)
                    .forEach((item) => {
                        counters.total++;
                        const isAttempted = isScorableItemAttempted(responseStatus, question.contentRevisionId, item);
                        if (isAttempted) {
                            counters.attempted++;
                        }
                    });
            });
        } else {
            questions.forEach((question) => {
                if (question.objectType === objectTypes.DOCUMENT) {
                    return;
                }
                counters.total++;
                if (responseReader.isQuestionAttempted(question)) {
                    counters.attempted++;
                }
            });
        }

        return counters;
    },

    /**
     * Gets aria label for question section based on screen reader mode and section status
     * @param {object} responseStatus
     * @param {object} section
     * @param {boolean} useNumberPerInteraction
     * @returns {string|undefined} Aria label for question section or undefined
     */
    getAriaLabelForQuestionSection: (
        responseStatus,
        section,
        useNumberPerInteraction
    ) => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) return undefined;
        const { attempted, total } = footerSelector.getAttemptsStatusForSection(responseStatus, section, useNumberPerInteraction);
        const unanswered = total - attempted;
        if (!section?.questions || section?.questions?.length === 0) return undefined;

        let sectionNumber = section.questions[0].sectionNumber;
        if (section.customFields) {
            sectionNumber = customFieldsService.getCustomFieldValue(section.customFields, customFieldsConstants.SECTION);
        }

        const unansweredLabel = unanswered === 1
            ? t('navigation.sectionStatus.unansweredSingleQuestion', { sectionNumber, unanswered, total })
            : t('navigation.sectionStatus.unansweredMultipleQuestion', { sectionNumber, unanswered, total });

        return unansweredLabel;
    },

    /**
     * Gets aria label for tab list based on screen reader mode and assessment structure
     * @param {object} assessment - The assessment object
     * @param {object} settingValues - The settings values
     * @returns {string} Aria label for the tab list
     */
    getTabListAriaLabel: (assessment, settingValues) => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) return t('navigation.paginationTitle');

        const { numbering_scheme: numberingScheme } = settingValues || {};
        let numParts = 0;
        if (numberingScheme === numberingSchemes.TEST_SEQUENTIAL) {
            numParts = assessment?.sections ? assessment.sections.reduce((total, section) => {
                return total + section.questions.length;
            }, 0) : 0;
        } else {
            const sections = assessment?.mergedSections ? assessment.mergedSections : assessment.sections;
            numParts = sections ? sections.length : 0;
        }

        return t('navigation.tablistLabel', { numParts });
    },

    /**
     * Gets aria label for question based on screen reader mode and question status
     * @param {boolean} isAttempted - Whether the question has been attempted
     * @param {string} questionPrefix - The prefix for the question (e.g., "Question")
     * @param {string} itemLabel - The label for the question item (e.g., "1", "2", etc.)
     * @returns {string|undefined} Aria label for the question or undefined
     */
    getAriaLabelForQuestion: (isAttempted, questionPrefix, itemLabel) => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) return undefined;
        const key = isAttempted
            ? 'testOverview.questionAnswered.label'
            : 'testOverview.questionNotAnswered.label';
        return t(key, { questionPrefix, questionNumber: itemLabel });
    },

    /**
     * Gets accessibility attributes for question buttons in IIB screen reader mode
     * @param {number} contentRevisionId - The content revision ID for aria-controls
     * @param {boolean} isActiveItem - Whether this is the currently active item
     * @returns {object} Object containing aria attributes and role/tabIndex values
     */
    getQuestionButtonA11yAttributes: (contentRevisionId, isActiveItem) => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                ariaControls: undefined,
                ariaCurrent: undefined,
                role: undefined,
                tabIndex: 0,
                ariaSelected: undefined,
            };
        }

        return {
            ariaControls: `question${contentRevisionId}-panel`,
            ariaCurrent: isActiveItem ? 'page' : undefined,
            role: 'tab',
            tabIndex: isActiveItem ? 0 : -1,
            ariaSelected: isActiveItem,
        };
    },

    /**
     * Gets accessibility attributes for question section buttons in IIB screen reader mode
     * @param {boolean} isCurrentSection - Whether this is the currently active section
     * @returns {object} Object containing aria attributes and role values for section buttons
     */
    getQuestionSectionButtonA11yAttributes: (isCurrentSection) => {
        return {
            ariaSelected: isA11yIterationForIIBScreenReaderEnabled() ? isCurrentSection : undefined,
        };
    },

    /**
     * Gets accessibility attributes for question section wrapper in IIB screen reader mode
     * @returns {object} Object containing role attributes for section wrapper and button
     */
    getQuestionSectionWrapperA11yAttributes: () => {
        return {
            sectionWrapperRole: isA11yIterationForIIBScreenReaderEnabled() ? 'tablist' : undefined,
            sectionButtonRole: isA11yIterationForIIBScreenReaderEnabled() ? undefined : 'tab',
        };
    },

    /**
     * Gets aria labels for navigation buttons based on screen reader mode

     * @returns {object} Object containing aria labels for navigation buttons
     */
    getNavigationButtonAriaLabels: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                prevAriaLabel: t('navigation.previous'),
                nextAriaLabel: t('navigation.next'),
                navigationControlsLabel: t('navigation.controlsTitle'),
            };
        }

        return {
            prevAriaLabel: t('navigation.previousButtonAriaLabel'),
            nextAriaLabel: t('navigation.nextButtonAriaLabel'),
            navigationControlsLabel: undefined,
        };
    },

    /**
     * Gets aria label for question button based on screen reader mode and attempted status

     * @param {boolean} isQuestionAttempted - Whether the question has been attempted
     * @returns {string|undefined} Aria label for the question button or undefined
     */
    getQuestionButtonAriaLabel: (isQuestionAttempted) => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return undefined;
        }

        return isQuestionAttempted
            ? t('testOverview.thisQuestionAnswered')
            : t('testOverview.thisQuestionNotAnswered');
    },

    /**
     * Gets tabIndex for navigation tab list based on screen reader mode
     * @returns {number|undefined} TabIndex value for the tab list
     */
    getTabListTabIndex: () => {
        return isA11yIterationForIIBScreenReaderEnabled() ? 0 : undefined;
    },

    /**
     * Gets role for navigation tab list based on screen reader mode
     * @returns {string|undefined} Role value for the tab list
     */
    getNavRoleAsTabList: () => {
        return isA11yIterationForIIBScreenReaderEnabled() ? 'tablist' : undefined;
    },

    getOnKeyDownForQuestionNavigationInSections: () => {
        return isA11yIterationForIIBScreenReaderEnabled() ? footerService.handleOnKeyDownForQuestionNavigationInSections : undefined;
    },

    isA11yV5AndAboveSupported: () => a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v5),

    selectFooterTagName() {
        return this.isA11yV5AndAboveSupported() ? 'nav' : 'footer';
    },

    selectFooterAttributes() {
        return this.isA11yV5AndAboveSupported() ? {
            'id': 'playerFooter',
            'aria-label': t('navigation.landmarkLabel'),
        } : EMPTY_FOOTER_ATTRIBUTES;
    },

    selectProps(state) {
        const {
            api,
            assessment,
            currentlyShown,
            itemFlag: {
                flaggedItems,
            },
            progress,
            response,
            settings,
            ui,
        } = state;

        return {
            api,
            assessment,
            currentlyShown,
            flaggedItems,
            footerTagName: this.selectFooterTagName(),
            footerAttributes: this.selectFooterAttributes(),
            isLargeTest: this.selectIsLargeTest(currentlyShown.sectionId, assessment.sections),
            isNavigationAllowed: this.selectIsNavigationAllowed(),
            progress,
            // Included to trigger re-rendering when response status changes, but not on each and every response update.
            responseStatus: response.status,
            settingValues: settings.values,
            shouldRenderNavRow: this.selectShouldRenderNavRow(),
            showFooterNavButtons: this.shouldShowFooterNavButtons(),
            showFinalStepButton: navigationService.isFinalStepAvailable(),
            ui,
        };
    },
};

export default footerSelector;
